import React, { useState, useCallback } from 'react';
import { Customer } from '../../types';
import { useDashboardData, CustomerWithMetrics } from '../../hooks/useDashboardData';
import { useDashboardFilters } from '../../hooks/useDashboardFilters';
import TeamDashboardView from './TeamDashboardView';
import UpdateCustomerModal from '../UpdateCustomerModal';
import MotivationalLoader from '../MotivationalLoader';

const TeamDashboardContainer: React.FC = () => {
  const {
    loading,
    error,
    customers,
    setCustomers,
    loadDashboardData,
    calculateStats,
  } = useDashboardData();

  const {
    selectedSegment,
    setSelectedSegment,
    sortBy,
    setSortBy,
    sortedCustomers,
  } = useDashboardFilters(customers);

  const [editingCustomer, setEditingCustomer] = useState<CustomerWithMetrics | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const handleCustomerClick = useCallback((customerId: string) => {
    window.location.href = `/?customerId=${customerId}`;
  }, []);

  const handleEditCustomer = useCallback((customer: CustomerWithMetrics) => {
    setEditingCustomer(customer);
    setIsUpdateModalOpen(true);
  }, []);

  const handleCustomerUpdate = useCallback((updatedCustomer: Customer) => {
    setCustomers(prevCustomers => 
      prevCustomers.map(c => 
        c.id === updatedCustomer.id 
          ? { ...c, ...updatedCustomer }
          : c
      )
    );
    setIsUpdateModalOpen(false);
    setEditingCustomer(null);
    loadDashboardData();
  }, [setCustomers, loadDashboardData]);

  const handleRefresh = useCallback(() => {
    loadDashboardData(true);
  }, [loadDashboardData]);

  const stats = calculateStats();

  if (loading) {
    return <MotivationalLoader type="dashboard" size="lg" />;
  }

  if (error) {
    return (
      <div className="team-dashboard error" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '20px',
          maxWidth: '600px',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#c00', marginBottom: '10px' }}>Dashboard Loading Error</h3>
          <p className="error-message" style={{ marginBottom: '15px' }}>{error}</p>
          
          <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
            <p>Please try the following:</p>
            <ul style={{ textAlign: 'left', marginTop: '10px' }}>
              <li>Check if you're signed in with the correct Google account</li>
              <li>Ensure you have access to the Google Sheets spreadsheet</li>
              <li>Check your internet connection</li>
              <li>Open the browser console (F12) for detailed error information</li>
            </ul>
          </div>
          
          <button 
            onClick={() => loadDashboardData(true)} 
            className="retry-button"
            style={{
              backgroundColor: '#4285f4',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <TeamDashboardView
        customers={sortedCustomers}
        stats={stats}
        selectedSegment={selectedSegment}
        sortBy={sortBy}
        onSegmentChange={setSelectedSegment}
        onSortChange={setSortBy}
        onCustomerClick={handleCustomerClick}
        onEditCustomer={handleEditCustomer}
        onRefresh={handleRefresh}
      />
      
      {editingCustomer && (
        <UpdateCustomerModal
          isOpen={isUpdateModalOpen}
          onClose={() => {
            setIsUpdateModalOpen(false);
            setEditingCustomer(null);
          }}
          customer={editingCustomer}
          onUpdate={handleCustomerUpdate}
        />
      )}
    </>
  );
};

export default TeamDashboardContainer;